import { CustomFontFamily } from "@redotech/redo-content-builder-common/brand-kit";
import { MarketingForgeElementType } from "@redotech/redo-content-builder-common/element-schema";
import {
  SpotlightElement,
  SpotlightForm,
} from "@redotech/redo-spotlight-common/spotlight-schema";
import { memo, useMemo } from "react";
import { useFontLoader } from "../../form-builder/inspectors/custom-fonts/hooks/use-font-loader";

function collectFontFamilies(elements: SpotlightElement[]): Set<string> {
  const fonts = new Set<string>();
  for (const element of elements) {
    if ("fontFamily" in element && element.fontFamily) {
      fonts.add(element.fontFamily as string);
    }
    if (
      (element.type === MarketingForgeElementType.BUTTON ||
        element.type === MarketingForgeElementType.TEXT_TO_SUBSCRIBE_BUTTON) &&
      element.styleOverride?.font?.fontFamily
    ) {
      fonts.add(element.styleOverride.font.fontFamily);
    }
    if (
      (element.type === MarketingForgeElementType.CONTAINER ||
        element.type === MarketingForgeElementType.COLUMN) &&
      element.elements
    ) {
      for (const f of collectFontFamilies(
        element.elements as SpotlightElement[],
      )) {
        fonts.add(f);
      }
    }
  }
  return fonts;
}

const SingleFontLoader = memo(function SingleFontLoader({
  customFont,
}: {
  customFont: CustomFontFamily;
}) {
  const firstStyle = customFont.styles[0];

  useFontLoader({
    fontFileUrl: firstStyle?.fontFileUrl || "",
    fontFamily: customFont.fontFamily,
    fontWeight: firstStyle?.weight || "normal",
    fontStyle: firstStyle?.italic ? "italic" : "normal",
    fontId: firstStyle?._id,
    persistOnUnmount: true,
  });

  return null;
});

interface SpotlightFontLoaderProps {
  spotlight: SpotlightForm;
  customFontFamilies?: CustomFontFamily[] | null;
}

export const SpotlightFontLoader = memo(function SpotlightFontLoader({
  spotlight,
  customFontFamilies,
}: SpotlightFontLoaderProps) {
  const customFontsToLoad = useMemo(() => {
    if (!customFontFamilies?.length) {
      return [];
    }
    const allElements = spotlight.steps.flatMap((s) => s.elements);
    const allFontNames = collectFontFamilies(allElements);
    return customFontFamilies.filter((cf) => allFontNames.has(cf.fontFamily));
  }, [spotlight.steps, customFontFamilies]);

  return (
    <>
      {customFontsToLoad.map((font) => (
        <SingleFontLoader customFont={font} key={font.fontFamily} />
      ))}
    </>
  );
});
